"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Shield, UserPlus, Users } from "lucide-react";
import { api } from "@/lib/axios";
import type { User } from "@/lib/types/user";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AccountActivePill,
  apiErr,
  buildRowRoles,
  initials,
  normalizeRoleId,
  type RoleRow,
} from "@/app/(dashboard)/admin/users/user-admin-shared";

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["admin", "roles"],
    queryFn: async () => {
      const { data: d } = await api.get<{ roles: RoleRow[] }>("/api/roles");
      return (d.roles ?? []).map((r) => ({
        ...r,
        id: Number((r as unknown as { id: string | number }).id),
      }));
    },
  });
  const rolesAll = rolesData ?? [];
  const roleOptions = useMemo(() => rolesAll.filter((r) => r.name !== "product_admin"), [rolesAll]);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data: d } = await api.get<{ users: User[] }>("/api/users");
      return (d.users ?? []).map((u) => ({
        ...u,
        role_id: normalizeRoleId(u.role_id as string | number | null | undefined) as unknown as User["role_id"],
      }));
    },
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState<number | "">("");

  useEffect(() => {
    if (roleId === "" && roleOptions.length > 0) {
      const cashier = roleOptions.find((r) => r.name === "cashier") ?? roleOptions[0];
      setRoleId(cashier?.id ?? "");
    }
  }, [roleId, roleOptions]);

  const create = useMutation({
    mutationFn: async () => {
      if (typeof roleId !== "number") {
        throw new Error("Choose a role");
      }
      await api.post("/api/users", { name, email, password, role_id: roleId, is_active: 1 });
    },
    onSuccess: async () => {
      setName("");
      setEmail("");
      setPassword("");
      toast.success("User created");
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const updateUser = useMutation({
    mutationFn: async (u: User & { password?: string; role_id?: number | null }) => {
      const body: Record<string, unknown> = {
        name: u.name,
        email: u.email,
        role_id: u.role_id ?? null,
        branch_id: u.branch_id,
        is_active: u.is_active,
      };
      if (typeof u.role_id === "number" && u.role_id > 0) {
        const roleName = rolesAll.find((r) => r.id === u.role_id)?.name;
        if (roleName) {
          body.role = roleName;
        }
      }
      if (u.password) {
        body.password = u.password;
      }
      const { data } = await api.put<{ user?: User }>(`/api/users/${u.id}`, body);
      const updated = data?.user;
      if (!updated) {
        throw new Error("Server response missing user");
      }
      return updated;
    },
    onSuccess: async (updated) => {
      toast.success("User updated");
      if (!updated) {
        await qc.invalidateQueries({ queryKey: ["admin", "users"] });
        return;
      }
      qc.setQueryData<User[]>(["admin", "users"], (prev: User[] | undefined) => {
        if (!prev) {
          return prev;
        }
        const role_id = normalizeRoleId(updated.role_id as string | number | null | undefined) as unknown as User["role_id"];
        const merged: User = { ...updated, role_id };

        return prev.map((row: User) => (row.id === merged.id ? { ...row, ...merged } : row));
      });
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const users = data ?? [];
  const activeCount = useMemo(() => users.filter((u) => u.is_active === 1).length, [users]);

  if (isLoading) {
    return (
      <div className="min-h-full space-y-6 bg-background p-6">
        <div className="h-36 animate-pulse rounded-2xl bg-muted/60" />
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="h-80 animate-pulse rounded-xl bg-muted/50 lg:col-span-2" />
          <div className="h-96 animate-pulse rounded-xl bg-muted/50 lg:col-span-3" />
        </div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="min-h-full bg-background p-6">
        <p className="text-sm text-destructive">
          {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Failed to load users."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-8 bg-background p-6">
      <section className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-primary/15 blur-3xl"
        />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-inner">
              <Users className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">Team & access</h1>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                Invite colleagues, assign roles, and control who can sign in. Changes apply on next login for
                permissions.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-center min-w-[100px]">
              <p className="text-2xl font-semibold tabular-nums text-foreground">{users.length}</p>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Accounts</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-center min-w-[100px]">
              <p className="text-2xl font-semibold tabular-nums text-primary">{activeCount}</p>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Active</p>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="border-border shadow-sm lg:col-span-2">
          <CardHeader className="border-b border-border bg-muted/30 pb-4">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Add user</CardTitle>
            </div>
            <CardDescription>Creates login for your company. Share password securely out of band.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 pt-6">
            <div className="grid gap-2">
              <Label htmlFor="nu-name">Full name</Label>
              <Input id="nu-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="nu-email">Work email</Label>
              <Input
                id="nu-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="nu-pw">Initial password</Label>
              <Input
                id="nu-pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select
                value={roleId === "" ? null : roleId}
                onValueChange={(v) => setRoleId(v == null ? "" : Number(v))}
                disabled={rolesLoading || roleOptions.length === 0}
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue placeholder={rolesLoading ? "Loading roles…" : "Choose role"}>
                    {(value: unknown) => {
                      const id = typeof value === "number" ? value : Number(value);
                      if (!Number.isFinite(id)) {
                        return null;
                      }
                      return roleOptions.find((r) => r.id === id)?.name ?? `Role #${id}`;
                    }}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id} label={r.name}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Separator className="my-1" />
            <Button
              type="button"
              className="w-full"
              disabled={create.isPending || !name.trim() || !email.trim() || !password || typeof roleId !== "number"}
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Creating…" : "Create account"}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm lg:col-span-3">
          <CardHeader className="border-b border-border bg-muted/30 pb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Directory</CardTitle>
            </div>
            <CardDescription>Update role, active state, or set a new password.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/20 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Active</th>
                    <th className="px-4 py-3">Password</th>
                    <th className="px-4 py-3 text-right"> </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      roles={buildRowRoles(u, roleOptions, rolesAll)}
                      onSave={(x) => updateUser.mutate(x)}
                      busy={updateUser.isPending}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 ? (
              <p className="border-t border-border px-4 py-8 text-center text-sm text-muted-foreground">
                No users yet — create the first account on the left.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function UserRow({
  user,
  onSave,
  busy,
  roles,
}: {
  user: User;
  onSave: (u: User & { password?: string; role_id?: number | null }) => void;
  busy: boolean;
  roles: RoleRow[];
}) {
  const resolvedId =
    normalizeRoleId(user.role_id as string | number | null | undefined) ??
    roles.find((r) => r.name === user.role)?.id ??
    null;
  const [roleId, setRoleId] = useState<number | "">(resolvedId ?? "");
  const [active, setActive] = useState(user.is_active === 1);
  const [pw, setPw] = useState("");

  useEffect(() => {
    const rid =
      normalizeRoleId(user.role_id as string | number | null | undefined) ??
      roles.find((r) => r.name === user.role)?.id ??
      null;
    setRoleId(rid ?? "");
    setActive(user.is_active === 1);
  }, [user.id, user.role_id, user.role, user.is_active, roles]);

  return (
    <tr className="border-b border-border/80 transition-colors hover:bg-muted/20">
      <td className="px-4 py-3">
        <Link
          to={`/admin/users/${user.id}`}
          className="flex items-center gap-3 rounded-lg outline-none ring-offset-background transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring -m-1 p-1"
        >
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary"
            aria-hidden
          >
            {initials(user.name)}
          </div>
          <div className="min-w-0 text-left">
            <p className="font-medium text-foreground truncate">{user.name}</p>
            <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
              <Mail className="h-3 w-3 shrink-0 opacity-70" />
              {user.email}
            </p>
          </div>
        </Link>
      </td>
      <td className="px-4 py-3 align-top">
        <Select
          value={roleId === "" ? null : roleId}
          onValueChange={(v) => setRoleId(v == null ? "" : Number(v))}
        >
          <SelectTrigger className="h-9 w-[min(100%,12rem)] min-w-[9rem]">
            <SelectValue placeholder="Role">
              {(value: unknown) => {
                const id = typeof value === "number" ? value : Number(value);
                if (!Number.isFinite(id)) {
                  return null;
                }
                return roles.find((r) => r.id === id)?.name ?? `Role #${id}`;
              }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id} label={r.name}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3 align-middle">
        <AccountActivePill checked={active} disabled={busy} onCheckedChange={(v) => setActive(v)} />
      </td>
      <td className="px-4 py-3 align-top">
        <Input
          className="h-9 max-w-[11rem]"
          type="password"
          placeholder="Optional reset"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoComplete="new-password"
        />
      </td>
      <td className="px-4 py-3 text-right align-top">
        <Button
          type="button"
          size="sm"
          disabled={busy || typeof roleId !== "number"}
          onClick={() => {
            onSave({
              ...user,
              role_id: typeof roleId === "number" ? roleId : null,
              is_active: active ? 1 : 0,
              password: pw || undefined,
            });
            setPw("");
          }}
        >
          Save
        </Button>
      </td>
    </tr>
  );
}
